import { useCallback, useEffect, useState } from 'react';
import { MagnifyingGlass, Plus, Tag, Trash, PencilSimple } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { mktApi, errMsg } from './marketingApi';
import CustomerFormDialog from './CustomerFormDialog';
import PriceListDialog from './PriceListDialog';

const CLOSING = (d) => (d == null ? '—' : d === 99 ? '末日' : `${d}日`);

export default function Customers() {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [priceCustomer, setPriceCustomer] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (q.trim()) params.q = q.trim();
      if (statusFilter) params.status = statusFilter;
      setCustomers(await mktApi.customers(params));
    } catch (e) { toast.error(errMsg(e, '客先の取得に失敗しました')); }
    finally { setLoading(false); }
  }, [q, statusFilter]);

  useEffect(() => {
    const tm = setTimeout(load, 250);
    return () => clearTimeout(tm);
  }, [load]);

  const remove = async (c) => {
    if (!window.confirm(`客先「${c.name}」を削除しますか？`)) return;
    try { await mktApi.deleteCustomer(c.id); toast.success('客先を削除しました'); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="space-y-4" data-testid="mkt-customers-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold tracking-tight text-zinc-900">客先マスタ</h1>
          <p className="text-xs text-zinc-500 mt-0.5">受注に使用する客先を管理します（仕入先マスタと同じ操作感）</p>
        </div>
        <Button onClick={() => { setEditing(null); setFormOpen(true); }} className="bg-teal-700 hover:bg-teal-800" data-testid="mkt-customer-create-btn">
          <Plus size={15} className="mr-1.5" /> 新規客先
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="客先名・かな・コードで検索" className="pl-8 h-9" data-testid="mkt-customers-search" />
        </div>
        {['', 'active', 'inactive'].map((sfx) => (
          <button key={sfx || 'all'} type="button" onClick={() => setStatusFilter(sfx)} data-testid={`mkt-customers-filter-${sfx || 'all'}`}
            className={`rounded-full border px-2.5 py-1 text-[11px] font-medium transition-colors ${statusFilter === sfx ? 'border-fuchsia-500 bg-fuchsia-50 text-fuchsia-700' : 'border-zinc-200 bg-white text-zinc-500 hover:bg-zinc-50'}`}>
            {sfx === '' ? 'すべて' : sfx === 'active' ? '有効' : '無効'}
          </button>
        ))}
        <span className="ml-auto text-[11px] text-zinc-400" data-testid="mkt-customers-count">{customers.length}件</span>
      </div>

      <div className="rounded-xl border border-zinc-200 bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-zinc-200 bg-zinc-50/70 text-left text-[11px] uppercase tracking-wide text-zinc-400">
                <th className="px-4 py-2.5 font-medium">客先名</th>
                <th className="px-3 py-2.5 font-medium">コード</th>
                <th className="px-3 py-2.5 font-medium">担当者</th>
                <th className="px-3 py-2.5 font-medium">TEL</th>
                <th className="px-3 py-2.5 font-medium">締め日</th>
                <th className="px-3 py-2.5 font-medium">支払条件</th>
                <th className="px-3 py-2.5 font-medium">状態</th>
                <th className="px-4 py-2.5" />
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={8} className="px-4 py-10 text-center text-xs text-zinc-400">読み込み中…</td></tr>}
              {!loading && customers.length === 0 && (
                <tr><td colSpan={8} className="px-4 py-12 text-center text-sm text-zinc-400" data-testid="mkt-customers-empty">客先がありません。「新規客先」から登録してください</td></tr>
              )}
              {!loading && customers.map((c) => (
                <tr key={c.id} className="border-b border-zinc-50 hover:bg-fuchsia-50/30 transition-colors" data-testid={`mkt-customer-row-${c.id}`}>
                  <td className="px-4 py-3">
                    <div className="font-medium text-zinc-900">{c.name}</div>
                    {c.kana && <div className="text-[10px] text-zinc-400">{c.kana}</div>}
                  </td>
                  <td className="px-3 py-3 font-mono text-xs text-zinc-500">{c.code || '—'}</td>
                  <td className="px-3 py-3 text-xs text-zinc-600">{c.contact || '—'}</td>
                  <td className="px-3 py-3 text-xs tabular-nums text-zinc-600">{c.phone || '—'}</td>
                  <td className="px-3 py-3 text-xs text-zinc-600">{CLOSING(c.closing_day)}</td>
                  <td className="px-3 py-3 text-xs text-zinc-600">{c.payment_terms || '—'}</td>
                  <td className="px-3 py-3">
                    <span className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold ${c.status === 'inactive' ? 'bg-zinc-100 text-zinc-400 border-zinc-200' : 'bg-emerald-50 text-emerald-700 border-emerald-200'}`}>
                      {c.status === 'inactive' ? '無効' : '有効'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <button type="button" onClick={() => setPriceCustomer(c)} title="販売単価マスタ" className="text-zinc-400 hover:text-fuchsia-600 transition-colors mr-3" data-testid={`mkt-customer-prices-${c.id}`}><Tag size={16} /></button>
                    <button type="button" onClick={() => { setEditing(c); setFormOpen(true); }} className="text-zinc-400 hover:text-fuchsia-600 transition-colors mr-3" data-testid={`mkt-customer-edit-${c.id}`}><PencilSimple size={16} /></button>
                    <button type="button" onClick={() => remove(c)} className="text-zinc-400 hover:text-red-500 transition-colors" data-testid={`mkt-customer-delete-${c.id}`}><Trash size={16} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <CustomerFormDialog open={formOpen} onOpenChange={setFormOpen} customer={editing}
        onSaved={() => { setFormOpen(false); load(); }} />
      <PriceListDialog open={!!priceCustomer} onOpenChange={(v) => { if (!v) setPriceCustomer(null); }} customer={priceCustomer} />
    </div>
  );
}
